import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Store, Download, Lock, Clock, HelpCircle, ChevronRight, ChevronDown,
  FolderTree, Folder, FileText, Layers,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, EmptyState, AppButton } from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { assessmentMarketplaceTrail } from "../QuizBank/quizCenterTrail";
import "./Assessments.scss";
import "./AssessmentMarketplace.scss";

const MODE_LABELS = { practice: "Practice", exam: "Exam", sectioned_exam: "Sectioned" };
const DIFF_LABELS = { easy: "Easy", medium: "Medium", hard: "Hard" };

// Human-readable price for a paid listing; null for free.
const fmtPrice = (p) => {
  if (!p || p.model !== "paid") return null;
  const sym = p.currency === "INR" ? "₹" : `${p.currency || ""} `;
  const per =
    p.period === "monthly" ? "/mo" :
    p.period === "yearly" ? "/yr" :
    p.period === "lifetime" ? " lifetime" : "";
  return `${sym}${p.amount || 0}${per}`;
};

/**
 * AssessmentMarketplacePage — dedicated institute page (replaces the old popup)
 * for browsing assessments shared to the platform marketplace. Each item can be
 * previewed as a folderized question tree (category → subtopic → questions) before
 * importing. Importing deep-clones the assessment + questions into the institute
 * as a draft. Paid items need a granted entitlement.
 */
const AssessmentMarketplacePage = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());   // assessmentId open for preview
  const [previews, setPreviews] = useState({});                // id → { tree, total, dynamic, loading }
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/assessments/marketplace");
      setItems(data.assessments || []);
    } catch {
      setToast({ show: true, message: "Failed to load marketplace", type: "error" });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Lazy-load the folderized question preview the first time a card is expanded.
  const loadPreview = async (id) => {
    setPreviews((m) => ({ ...m, [id]: { ...(m[id] || {}), loading: true } }));
    try {
      const { data } = await apiClient.get(`/assessments/${id}/marketplace-preview`);
      setPreviews((m) => ({ ...m, [id]: { tree: data.tree || [], total: data.total || 0, dynamic: !!data.dynamic, loading: false } }));
    } catch {
      setPreviews((m) => ({ ...m, [id]: { tree: [], total: 0, dynamic: false, loading: false } }));
    }
  };

  const togglePreview = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else { next.add(id); if (!previews[id]) loadPreview(id); }
      return next;
    });
  };

  const handleImport = async (id) => {
    setImportingId(id);
    try {
      const { data } = await apiClient.post(`/assessments/${id}/import-marketplace`);
      setToast({ show: true, message: "Imported as a draft", type: "success" });
      if (data.assessment?._id) navigate(`/assessments/edit/${data.assessment._id}`);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Import failed", type: "error" });
    }
    setImportingId(null);
  };

  return (
    <div className="assessment-marketplace">
      <PageHeader
        icon={Store}
        title="Assessment Marketplace"
        description="Ready-made assessments shared across the platform. Preview the questions, then import a copy into your institute as a draft."
        breadcrumbs={assessmentMarketplaceTrail()}
      />

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Store size={40} />}
          title="Nothing here yet"
          description="No assessments have been shared to the marketplace."
        />
      ) : (
        <div className="assessment-list">
          {items.map((m) => {
            const paidLocked = m.pricing?.model === "paid" && !m.entitled;
            const open = expanded.has(m._id);
            const pv = previews[m._id];
            return (
              <div className="assessment-card" key={m._id}>
                <div className="assessment-card__header">
                  <div className="flex-grow-1">
                    <div className="assessment-card__badges">
                      <span className="assessment-card__mode">{MODE_LABELS[m.mode] || m.mode}</span>
                      <span className="assessment-card__selection">
                        {m.selectionMode === "dynamic" ? "Dynamic" : "Manual"}
                      </span>
                      {m.marketplaceCategory && <span className="assessment-card__selection">{m.marketplaceCategory}</span>}
                      {m.pricing?.model === "paid"
                        ? <span className="assessment-card__price">{fmtPrice(m.pricing)}</span>
                        : <span className="assessment-card__free">Free</span>}
                    </div>
                    <h3 className="assessment-card__title" style={{ cursor: "default" }}>{m.title}</h3>
                    {m.description && <p className="assessment-card__description">{m.description}</p>}
                    <div className="assessment-card__meta">
                      <span className="assessment-card__meta-item"><HelpCircle size={14} /> {m.questionCount || 0} questions</span>
                      {m.timeLimit > 0 && <span className="assessment-card__meta-item"><Clock size={14} /> {m.timeLimit} min</span>}
                    </div>
                  </div>
                  <div className="assessment-card__actions amkt-actions">
                    {paidLocked ? (
                      <div className="assessment-locked" title="Contact the platform admin for access">
                        <Lock size={14} />
                        <span>{fmtPrice(m.pricing)}</span>
                        <small>Requires access</small>
                      </div>
                    ) : (
                      <AppButton
                        variant="default"
                        size="sm"
                        disabled={importingId === m._id}
                        onClick={() => handleImport(m._id)}
                      >
                        <Download size={14} /> {importingId === m._id ? "Importing…" : "Import"}
                      </AppButton>
                    )}
                    <AppButton variant="outline" size="sm" onClick={() => togglePreview(m._id)}>
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Preview questions
                    </AppButton>
                  </div>
                </div>

                {open && (
                  <div className="amkt-preview">
                    {pv?.loading ? (
                      <div className="amkt-preview-loading"><Spinner animation="border" size="sm" /></div>
                    ) : pv?.dynamic ? (
                      <div className="amkt-preview-note">
                        <Layers size={15} /> Questions are picked dynamically at attempt time, so there's no fixed list to preview.
                      </div>
                    ) : !pv || pv.tree.length === 0 ? (
                      <div className="amkt-preview-note">No questions to preview.</div>
                    ) : (
                      <>
                        <div className="amkt-preview-head">
                          <FolderTree size={14} /> {pv.total} question{pv.total === 1 ? "" : "s"} across {pv.tree.length} categor{pv.tree.length === 1 ? "y" : "ies"}
                        </div>
                        {pv.tree.map((cat) => (
                          <PreviewCategory key={cat._id || cat.name} cat={cat} />
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

// ── A single category block in the folderized preview ───────────────────────
const PreviewCategory = ({ cat }) => (
  <div className="amkt-cat">
    <div className="amkt-cat-head">
      <Folder size={15} className="amkt-folder-icon" />
      <span className="amkt-cat-name">{cat.icon ? `${cat.icon} ` : ""}{cat.name}</span>
      <span className="amkt-cat-count">{cat.count}</span>
    </div>

    {cat.directQuestions?.length > 0 && <QuestionRows items={cat.directQuestions} />}

    {cat.subtopics?.map((sub) => (
      <div className="amkt-sub" key={sub._id || sub.name}>
        <div className="amkt-sub-head">
          <FileText size={13} className="amkt-folder-icon" />
          <span className="amkt-sub-name">{sub.name}</span>
          <span className="amkt-cat-count">{sub.questions.length}</span>
        </div>
        <QuestionRows items={sub.questions} />
      </div>
    ))}
  </div>
);

const QuestionRows = ({ items }) => (
  <ul className="amkt-qlist">
    {items.map((q) => (
      <li className="amkt-qrow" key={q._id}>
        <span className="amkt-qtext">{q.questionText}</span>
        <span className="amkt-qtype">{q.questionType?.replace(/_/g, " ")}</span>
        {q.difficulty && <span className={`amkt-qdiff amkt-qdiff--${q.difficulty}`}>{DIFF_LABELS[q.difficulty] || q.difficulty}</span>}
        <span className="amkt-qmarks">{q.marks} mk</span>
      </li>
    ))}
  </ul>
);

export default AssessmentMarketplacePage;
